import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Play,
  FileText,
  X,
  Loader2,
  AlertCircle,
  FolderOpen,
  CheckCircle,
  Star,
  LogIn,
  LogOut,
  User,
  Upload,
  FileSpreadsheet,
  Link2,
  Plus,
  Archive,
  CalendarDays,
  Clock3,
  Trash2,
  Download,
  CircleHelp,
  BarChart3,
  LayoutList,
  FileType,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import ParameterForm from './ParameterForm';
import UploadStatus from './UploadStatus';
import Connections from './Connections';
import ToolLogin from './ToolLogin';
import ReportHistory from './ReportHistory';
import ExternalIdentifierLookup from './ExternalIdentifierLookup';
import PayrollHoursByTimeTypeReport from './PayrollHoursByTimeTypeReport';
import TimeCardHcmDeletePanel from './TimeCardHcmDeletePanel';
import TimeCardsListPanel from './TimeCardsListPanel';
import timeAbsenceReportsSeed from './timeAbsenceReportsSeed.json';
import { getApiBase } from './apiBase';
import LandingPage from './LandingPage';

// Send cookies so session is used (withCredentials + backend CORS credentials: true)
// API base: REACT_APP_API_URL if set; else on localhost/127.0.0.1 use '' (CRA proxy → :3002, same-origin cookies).
const API_BASE = getApiBase();
const API_BASE_SOURCE = (() => {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
    return 'env (REACT_APP_API_URL)';
  const hostname = typeof window !== 'undefined' ? window.location?.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1')
    return 'same origin (dev proxy → :3002)';
  return 'same origin';
})();
const API = axios.create({ baseURL: API_BASE, timeout: 30000, withCredentials: true });

// Derive category from report path: /Custom/SAAS Tool/V1/Payroll/... -> "Payroll"
const CATALOG_ROOT = '/Custom/SAAS Tool/V1';
function getCategoryFromPath(path) {
  if (!path) return null;
  if (String(path).startsWith('recipe:')) return 'SQL Recipes';
  if (!path.startsWith(CATALOG_ROOT + '/')) return null;
  const rest = path.slice((CATALOG_ROOT + '/').length).trim();
  const firstSlash = rest.indexOf('/');
  return firstSlash > 0 ? rest.slice(0, firstSlash) : rest || null;
}

/** True if path or name ends with .xdo (catalog shows only .xdo reports). */
function isXdoReport(pathOrName) {
  return pathOrName && /\.xdo$/i.test(String(pathOrName).trim());
}

// Unique categories from reports list
function getCategories(reports) {
  const set = new Set();
  reports.forEach((r) => {
    const cat = getCategoryFromPath(r.path);
    if (cat) set.add(cat);
  });
  return Array.from(set).sort();
}

// Filter reports by category (.xdo BIP reports + imported SQL recipes)
function filterReports(reports, category) {
  let list = reports.filter((r) => isXdoReport(r.path) || isSqlRecipeReport(r));
  if (category) {
    list = list.filter((r) => getCategoryFromPath(r.path) === category);
  }
  return list;
}

function isTimeRelatedReport(report) {
  const haystack = `${report?.name || ''} ${report?.path || ''}`.toLowerCase();
  return /\btime\b|timesheet|time ?sheet|timecard|time card|time entry|time change|time and labor|\blabor\b|otl|hxt|punch|clock|attendance|payroll time|absence|leave|pto|roster|employee roster|worker roster/.test(haystack);
}

function isSqlRecipeReport(report) {
  return String(report?.source || '') === 'sqlRecipe' || String(report?.path || '').startsWith('recipe:');
}

function isRunnableSqlRecipe(report) {
  if (!isSqlRecipeReport(report)) return true;
  return Boolean(String(report?.sql || '').trim());
}

function buildParameterNameValues(params, paramValues) {
  return params
    .filter((p) => paramValues[p.name] !== undefined && paramValues[p.name] !== '')
    .map((p) => ({
      name: p.name,
      values: Array.isArray(paramValues[p.name]) ? paramValues[p.name] : [paramValues[p.name]],
    }));
}

/** Parse run result data into an array of row objects for table display. */
function parsePreviewRows(data) {
  if (data == null) return [];
  let parsed = data;
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object') {
    const arr = parsed.data ?? parsed.rows ?? parsed.records ?? parsed.results ?? parsed.items;
    if (Array.isArray(arr)) return arr;
    const keys = Object.keys(parsed);
    const firstArray = keys.map((k) => parsed[k]).find(Array.isArray);
    if (firstArray) return firstArray;
  }
  return [];
}

const PREVIEW_MAX_ROWS = 5;

const FAVORITES_STORAGE_KEY = 'hcm-reporting-favorites';
const SIDEBAR_COLLAPSED_KEY = 'tidesync.sidebar.collapsed';

function loadSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function saveSidebarCollapsed(collapsed) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? 'true' : 'false');
  } catch (_) {}
}

function SidebarNavButton({ collapsed, active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? label : undefined}
      aria-label={label}
      className={`flex w-full items-center rounded-lg py-2.5 text-sm font-medium transition-colors ${
        collapsed ? 'justify-center px-2' : 'gap-2.5 px-3 text-left'
      } ${
        active ? 'bg-cyan-700/80 text-white' : 'text-cyan-100 hover:bg-cyan-800/60 hover:text-white'
      }`}
    >
      <Icon className="h-4 w-4 shrink-0 text-cyan-400" />
      {!collapsed && <span className="truncate">{label}</span>}
    </button>
  );
}

function SidebarSectionLabel({ collapsed, children, spaced = false }) {
  if (collapsed) return null;
  return (
    <p
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-cyan-300/90${
        spaced ? ' mt-4' : ''
      }`}
    >
      {children}
    </p>
  );
}

function SidebarToggleButton({ collapsed, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? 'Expand sidebar' : 'Minimize sidebar'}
      aria-label={collapsed ? 'Expand sidebar' : 'Minimize sidebar'}
      className={`flex shrink-0 items-center justify-center rounded-md transition-colors ${
        collapsed ? 'h-9 w-9 p-2' : 'h-8 w-8 p-1.5'
      } text-amber-200 hover:bg-cyan-800/70 hover:text-amber-50`}
    >
      {collapsed ? (
        <PanelLeftOpen className="h-4 w-4" strokeWidth={2.25} />
      ) : (
        <PanelLeftClose className="h-4 w-4" strokeWidth={2.25} />
      )}
    </button>
  );
}

function loadFavorites() {
  try {
    const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveFavorites(paths) {
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(paths));
  } catch (_) {}
}

/** Shimmer skeleton for catalog loading */
function CatalogSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="h-5 w-3/4 rounded animate-shimmer" />
          <div className="mt-2 h-4 w-full rounded animate-shimmer" style={{ animationDelay: `${i * 50}ms` }} />
          <div className="mt-1 h-4 w-2/3 rounded animate-shimmer" style={{ animationDelay: `${i * 50 + 30}ms` }} />
          <div className="mt-4 h-9 w-full rounded-lg animate-shimmer" style={{ animationDelay: `${i * 50 + 60}ms` }} />
        </div>
      ))}
    </div>
  );
}

function PreviewTable({ rows }) {
  if (!rows || rows.length === 0) return null;
  const slice = rows.slice(0, PREVIEW_MAX_ROWS);
  const columns = Object.keys(slice[0] ?? {}).filter((k) => typeof (slice[0][k]) !== 'object' || slice[0][k] === null);
  if (columns.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
        <thead className="bg-slate-50">
          <tr>
            {columns.map((col) => (
              <th key={col} className="px-4 py-2 font-medium text-slate-700">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white">
          {slice.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td key={col} className="px-4 py-2 text-slate-800">
                  {row[col] != null ? String(row[col]) : '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Login modal for Oracle Cloud credentials */
function LoginModal({ onClose, onSuccess }) {
  const [instanceUrl, setInstanceUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    API.post('/auth/login', {
      instanceUrl: instanceUrl.trim() || undefined,
      username: username.trim(),
      password,
    })
      .then((res) => {
        if (res.data?.success && res.data?.user) {
          onSuccess(res.data.user);
          onClose();
        }
      })
      .catch((err) => {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Login failed';
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Oracle Cloud sign in</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-4 text-sm text-slate-600">
          Enter your Oracle Cloud HCM instance URL and your BI Publisher credentials.
        </p>
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-red-50 p-2 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
              {error}
            </div>
          )}
          <div className="mb-3">
            <label htmlFor="login-instance-url" className="block text-sm font-medium text-slate-700">
              Instance URL
            </label>
            <input
              id="login-instance-url"
              type="text"
              autoComplete="url"
              placeholder="your-instance.fa.ocs.oraclecloud.com"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-800 placeholder-slate-400 focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
            />
            <p className="mt-1 text-xs text-slate-500">
              Use the application host (e.g. iaxcqy-dev1.fa.ocs.oraclecloud.com). If you paste the login URL (login-iaxcqy-dev1...), we will use the correct host automatically.
            </p>
          </div>
          <div className="mb-3">
            <label htmlFor="login-username" className="block text-sm font-medium text-slate-700">
              Username
            </label>
            <input
              id="login-username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-800 focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="login-password" className="block text-sm font-medium text-slate-700">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-800 focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting…
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  Sign in
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function sqlRecipePreviewRows(runResult) {
  if (!runResult) return [];
  const rows = runResult.rows;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  if (typeof rows[0] === 'object' && !Array.isArray(rows[0])) return rows;
  const cols = (runResult.columns || [])
    .map((c) => (typeof c === 'string' ? c : c?.name))
    .filter(Boolean);
  if (cols.length && Array.isArray(rows[0])) {
    return rows.map((arr) =>
      Object.fromEntries(cols.map((name, i) => [name, arr[i] != null ? arr[i] : '']))
    );
  }
  return parsePreviewRows(runResult);
}

function sqlRecipeGridColumns(rows, columnMeta) {
  const fromMeta = (columnMeta || [])
    .map((c) => (typeof c === 'string' ? c : c?.name))
    .filter(Boolean);
  if (fromMeta.length) return fromMeta;
  const first = rows?.[0];
  if (!first || typeof first !== 'object') return [];
  return Object.keys(first).filter((k) => typeof first[k] !== 'object' || first[k] === null);
}

/** Excel-like scrollable grid for SQL recipe results (all rows returned). */
function SqlRecipeSpreadsheet({ rows, columns: columnMeta }) {
  const columns = useMemo(() => sqlRecipeGridColumns(rows, columnMeta), [rows, columnMeta]);
  if (!rows?.length || !columns.length) return null;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-slate-300 bg-[#f3f3f3] shadow-inner">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="border-collapse text-left text-xs">
          <thead>
            <tr>
              <th
                className="sticky left-0 top-0 z-30 min-w-[2.75rem] border border-slate-300 bg-slate-200 px-2 py-1.5 text-center font-semibold text-slate-600"
                scope="col"
              >
                #
              </th>
              {columns.map((col) => (
                <th
                  key={col}
                  className="sticky top-0 z-20 min-w-[7rem] max-w-[20rem] border border-slate-300 bg-slate-100 px-2 py-1.5 font-semibold text-slate-700 whitespace-nowrap"
                  scope="col"
                  title={col}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/90'}
              >
                <td
                  className={`sticky left-0 z-10 border border-slate-300 px-2 py-1 text-center font-medium text-slate-500 ${
                    rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                  }`}
                >
                  {rowIndex + 1}
                </td>
                {columns.map((col) => (
                  <td
                    key={col}
                    className="border border-slate-300 px-2 py-1 text-slate-800 whitespace-nowrap"
                    title={row[col] != null ? String(row[col]) : ''}
                  >
                    {row[col] != null && row[col] !== '' ? String(row[col]) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

async function downloadSqlRecipeExport(body, report, format) {
  const response = await fetch(`${API_BASE}/api/run-oracle-sql/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ ...body, format }),
  });
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody.error || errBody.message || `Export failed (${response.status})`);
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = (report.name || report.recipeId || 'recipe').replace(/[^\w.-]+/g, '_');
  a.download = `${safeName}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function SqlRecipeExportButton({ label, icon: Icon, busy, disabled, onClick }) {
  return (
    <button
      type="button"
      title={`Download ${label}`}
      disabled={disabled || busy}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin text-cyan-600" /> : <Icon className="h-4 w-4 text-cyan-700" />}
      {label}
    </button>
  );
}

function RunSqlRecipeModal({ report, connectionId, onClose }) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [exportBody, setExportBody] = useState(null);
  const [rowLimit, setRowLimit] = useState(100);
  const [exportBusy, setExportBusy] = useState(null);
  const [exportNotice, setExportNotice] = useState(null);

  const runRecipe = useCallback(async () => {
    if (!report?.sql) return;
    setRunning(true);
    setError(null);
    setRunResult(null);
    setExportBody(null);
    setExportNotice(null);
    const body = {
      sql: report.sql,
      connectionId: connectionId || undefined,
      rowLimit,
    };

    try {
      const res = await API.post('/api/run-oracle-sql', body, { timeout: 120000 });
      if (!res.data?.ok) {
        const msg =
          res.data?.error?.message ||
          (typeof res.data?.error === 'string' ? res.data.error : null) ||
          'Query failed';
        setError(msg);
        return;
      }
      setRunResult({
        columns: res.data.columns,
        rows: res.data.rows,
        rowCount: res.data.rowCount,
        elapsedMs: res.data.elapsedMs,
      });
      setExportBody(body);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to run SQL recipe');
    } finally {
      setRunning(false);
    }
  }, [report, connectionId, rowLimit]);

  useEffect(() => {
    if (!report?.sql) return;
    runRecipe();
    // Run once when the results view opens for this recipe (use Re-run after changing row limit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.path]);

  const downloadExport = async (format) => {
    if (!exportBody) return;
    setExportBusy(format);
    setExportNotice(null);
    setError(null);
    try {
      await downloadSqlRecipeExport(exportBody, report, format);
      setExportNotice(format);
    } catch (err) {
      setError(err.message || `Failed to download ${format.toUpperCase()}`);
    } finally {
      setExportBusy(null);
    }
  };

  if (!report) return null;
  const previewRows = sqlRecipePreviewRows(runResult);
  const hasResults = runResult != null && !running;
  const exportsReady = !!exportBody && !running;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative flex h-[min(92vh,900px)] w-full max-w-[min(96vw,1400px)] flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 shrink-0 text-slate-600" />
              <h2 className="truncate text-lg font-semibold text-slate-800">
                {report.name || report.recipeId}
              </h2>
            </div>
            <p className="mt-0.5 truncate text-sm text-slate-500">{report.recipeId || report.path}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {exportsReady && (
              <>
                <SqlRecipeExportButton
                  label="Excel"
                  icon={FileSpreadsheet}
                  busy={exportBusy === 'xlsx'}
                  disabled={!exportsReady}
                  onClick={() => downloadExport('xlsx')}
                />
                <SqlRecipeExportButton
                  label="CSV"
                  icon={FileText}
                  busy={exportBusy === 'csv'}
                  disabled={!exportsReady}
                  onClick={() => downloadExport('csv')}
                />
                <SqlRecipeExportButton
                  label="PDF"
                  icon={FileType}
                  busy={exportBusy === 'pdf'}
                  disabled={!exportsReady}
                  onClick={() => downloadExport('pdf')}
                />
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 py-4">
          {!report.sql && (
            <div className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              SQL text is not available for this recipe.
            </div>
          )}

          <div className="mb-3 flex shrink-0 flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span>Row limit</span>
              <input
                type="number"
                min={1}
                max={500}
                value={rowLimit}
                onChange={(e) => setRowLimit(Number(e.target.value) || 100)}
                disabled={running}
                className="w-24 rounded-lg border border-slate-200 px-3 py-1.5 text-sm shadow-sm disabled:bg-slate-100"
              />
            </label>
            <button
              type="button"
              disabled={running || !report.sql}
              onClick={runRecipe}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {running ? 'Running…' : 'Re-run'}
            </button>
          </div>

          {error && (
            <div className="mb-3 flex shrink-0 items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <span>{error}</span>
            </div>
          )}

          {exportNotice && (
            <div className="mb-3 flex shrink-0 items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-800">
              <CheckCircle className="h-4 w-4 shrink-0" />
              {exportNotice.toUpperCase()} download started.
            </div>
          )}

          {running && !runResult && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-slate-600">
              <Loader2 className="h-10 w-10 animate-spin text-cyan-600" />
              <p className="text-sm">Running query…</p>
            </div>
          )}

          {hasResults && previewRows.length > 0 && (
            <section className="flex min-h-0 flex-1 flex-col rounded-lg border border-slate-200 bg-slate-50/50 p-3">
              <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2">
                <CheckCircle className="h-5 w-5 shrink-0 text-green-600" aria-hidden />
                <h3 className="font-medium text-slate-800">Results</h3>
                {runResult.rowCount != null && (
                  <span className="text-sm text-slate-500">
                    {previewRows.length} of {runResult.rowCount} row{runResult.rowCount !== 1 ? 's' : ''}
                    {runResult.elapsedMs != null ? ` · ${runResult.elapsedMs} ms` : ''}
                  </span>
                )}
                <span className="text-xs text-slate-500">Scroll to explore · use Excel / CSV / PDF above to download</span>
              </div>
              <div className="min-h-[240px] flex-1">
                <SqlRecipeSpreadsheet rows={previewRows} columns={runResult.columns} />
              </div>
            </section>
          )}

          {hasResults && previewRows.length === 0 && !error && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 py-12 text-slate-500">
              <p className="text-sm">Query completed with no rows.</p>
              {exportsReady && (
                <p className="text-xs">You can still download Excel, CSV, or PDF from the toolbar above.</p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function RunReportModal({ report, initialFormat = 'pdf', onClose, onRunComplete }) {
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [paramValues, setParamValues] = useState({});
  const [runResult, setRunResult] = useState(null);
  const [lastRunFormat, setLastRunFormat] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(initialFormat);

  useEffect(() => {
    setSelectedFormat(initialFormat);
  }, [initialFormat]);

  useEffect(() => {
    if (!report?.path) return;
    setLoading(true);
    setError(null);
    API.get('/api/report-definition', { params: { reportPath: report.path } })
      .then((res) => {
        setDefinition(res.data);
        const initial = {};
        (res.data.parameters || []).forEach((p) => {
          const param = p.listOfParamNameValues?.[0] || p;
          const name = param.name;
          if (name && param.defaultValue != null) initial[name] = param.defaultValue;
        });
        setParamValues(initial);
      })
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [report?.path]);

  const params = useMemo(() => {
    if (!definition?.parameters) return [];
    return definition.parameters.map((p) => p.listOfParamNameValues?.[0] || p).filter(Boolean);
  }, [definition]);

  const runReportWithFormat = async (format) => {
    if (!report?.path) return;
    setRunning(true);
    setError(null);
    setRunResult(null);
    const formattedParams = buildParameterNameValues(params, paramValues);

    try {
      const response = await fetch(`${API_BASE}/api/run-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          reportPath: report.path,
          reportName: report.name,
          parameters: formattedParams,
          format,
        }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        setError(errBody.error || errBody.message || `Request failed (${response.status})`);
        return;
      }

      const blob = await response.blob();
      const isJson = format === 'json';

      if (isJson) {
        const text = await blob.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
        setRunResult({ data });
        setLastRunFormat(format);
        onRunComplete?.(data);
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `HCM_Report.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setLastRunFormat(format);
      }
    } catch (err) {
      setError(err.message || 'Failed to run report');
    } finally {
      setRunning(false);
    }
  };

  if (!report) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-800">Run report</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          <p className="mb-4 text-sm text-slate-600">{report.name || report.path}</p>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
            </div>
          )}

          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
              {error}
            </div>
          )}

          {!loading && definition && (
            <form onSubmit={(e) => e.preventDefault()}>
              <ParameterForm
                parameters={definition.parameters}
                value={paramValues}
                onChange={setParamValues}
                onRunReport={runReportWithFormat}
                loading={running}
                selectedFormat={selectedFormat}
                onFormatChange={setSelectedFormat}
              />

              {runResult && lastRunFormat === 'json' && (() => {
                const rows = parsePreviewRows(runResult.data);
                return (
                  <section className="mb-4 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 shrink-0 text-green-600" aria-hidden />
                      <h3 className="font-medium text-slate-800">Preview</h3>
                      <span className="text-sm text-green-700">Data fetch successful</span>
                    </div>
                    <p className="mb-2 text-xs text-slate-500">First {PREVIEW_MAX_ROWS} rows</p>
                    {rows.length > 0 ? (
                      <PreviewTable rows={rows} />
                    ) : (
                      <p className="text-sm text-slate-500">No tabular data to display.</p>
                    )}
                  </section>
                );
              })()}

              {runResult && lastRunFormat !== 'json' && (runResult.contentType?.includes('json') || runResult.contentType?.includes('text') || runResult.contentType?.includes('xml')) && (
                <div className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-800">
                  Report completed.
                  {typeof runResult.data === 'string' && runResult.data.length < 2000 && (
                    <pre className="mt-2 max-h-48 overflow-auto text-xs">{runResult.data}</pre>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function toMinutes(timeValue) {
  if (!timeValue || typeof timeValue !== 'string') return null;
  const match = timeValue.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

function hoursBetween(startTime, stopTime) {
  const start = toMinutes(startTime);
  const stop = toMinutes(stopTime);
  if (start == null || stop == null || stop <= start) return 0;
  return (stop - start) / 60;
}

function isoDateKey(dateValue) {
  if (!dateValue || typeof dateValue !== 'string') return '';
  const d = dateValue.trim();
  if (!d) return '';
  // Accepts yyyy-mm-dd and mm/dd/yyyy and normalizes to yyyy-mm-dd when possible.
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const parts = d.split(/[-/]/);
  if (parts.length === 3 && parts[2].length === 4) {
    const mm = String(Number(parts[0])).padStart(2, '0');
    const dd = String(Number(parts[1])).padStart(2, '0');
    const yyyy = parts[2];
    if (Number(mm) > 0 && Number(mm) <= 12 && Number(dd) > 0 && Number(dd) <= 31) return `${yyyy}-${mm}-${dd}`;
  }
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

function weekKeyFromDate(dateValue) {
  const key = isoDateKey(dateValue);
  if (!key) return '';
  const d = new Date(`${key}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.getDay();
  const deltaToMonday = (day + 6) % 7;
  d.setDate(d.getDate() - deltaToMonday);
  return d.toISOString().slice(0, 10);
}

function formatHours(hours) {
  return `${hours.toFixed(2)}h`;
}

function TimeSheetWorkbench({ previewData, title = 'NICE Time Sheet Workbench' }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const cards = Array.isArray(previewData?.timeCards) ? previewData.timeCards : [];
    const mapped = cards.map((card, idx) => ({
      id: `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`,
      personHC: String(card.personHC || '').trim(),
      date: isoDateKey(String(card.date || '').trim()),
      startTime: String(card.startTime || '').trim().slice(0, 5),
      stopTime: String(card.stopTime || '').trim().slice(0, 5),
      payrollAttribute: String(card.payrollAttribute || 'Regular Hours US').trim(),
      exception: card.exception || '',
    }));
    setRows(mapped);
  }, [previewData]);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        personHC: '',
        date: '',
        startTime: '',
        stopTime: '',
        payrollAttribute: 'Regular Hours US',
        exception: '',
      },
    ]);
  }, []);

  const removeRow = useCallback((id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const updateCell = useCallback((id, key, value) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  }, []);

  const validation = useMemo(() => {
    const errorsById = new Map();
    const buckets = new Map();

    rows.forEach((row) => {
      const rowErrors = [];
      if (!row.personHC.trim()) rowErrors.push('Missing person');
      if (!isoDateKey(row.date)) rowErrors.push('Missing or invalid date');
      const start = toMinutes(row.startTime);
      const stop = toMinutes(row.stopTime);
      if (start == null || stop == null) rowErrors.push('Invalid time format (HH:MM)');
      if (start != null && stop != null && stop <= start) rowErrors.push('Stop time must be after start time');
      if (rowErrors.length > 0) errorsById.set(row.id, rowErrors);

      const dateKey = isoDateKey(row.date);
      if (row.personHC && dateKey && start != null && stop != null && stop > start) {
        const key = `${row.personHC}__${dateKey}`;
        const list = buckets.get(key) || [];
        list.push({ id: row.id, start, stop });
        buckets.set(key, list);
      }
    });

    buckets.forEach((items) => {
      items.sort((a, b) => a.start - b.start);
      for (let i = 1; i < items.length; i += 1) {
        if (items[i].start < items[i - 1].stop) {
          const prev = errorsById.get(items[i - 1].id) || [];
          prev.push('Overlaps another entry');
          errorsById.set(items[i - 1].id, prev);
          const cur = errorsById.get(items[i].id) || [];
          cur.push('Overlaps another entry');
          errorsById.set(items[i].id, cur);
        }
      }
    });

    const validRows = rows.filter((r) => !errorsById.has(r.id));
    return { errorsById, validRows };
  }, [rows]);

  const totals = useMemo(() => {
    const byDay = new Map();
    const byWeek = new Map();

    validation.validRows.forEach((row) => {
      const dayKey = `${row.personHC}__${isoDateKey(row.date)}`;
      const wkKey = `${row.personHC}__${weekKeyFromDate(row.date)}`;
      const hrs = hoursBetween(row.startTime, row.stopTime);
      byDay.set(dayKey, (byDay.get(dayKey) || 0) + hrs);
      byWeek.set(wkKey, (byWeek.get(wkKey) || 0) + hrs);
    });

    const weeklySummary = Array.from(byWeek.entries()).map(([key, hours]) => {
      const [personHC, weekStart] = key.split('__');
      const overtime = Math.max(0, hours - 40);
      return { personHC, weekStart, hours, overtime };
    });

    return { byDay, weeklySummary };
  }, [validation.validRows]);

  const handleExportCsv = () => {
    const header = ['PersonHC', 'Date', 'StartTime', 'StopTime', 'PayrollAttribute', 'Hours', 'Validation'];
    const lines = rows.map((row) => {
      const errs = validation.errorsById.get(row.id) || [];
      const fields = [
        row.personHC,
        isoDateKey(row.date),
        row.startTime,
        row.stopTime,
        row.payrollAttribute,
        hoursBetween(row.startTime, row.stopTime).toFixed(2),
        errs.join('; '),
      ];
      return fields.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',');
    });
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nice-timesheet-workbench.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = () => {
    const win = window.open('', '_blank', 'noopener,noreferrer,width=1000,height=700');
    if (!win) return;
    const rowsHtml = rows.map((row) => {
      const errs = (validation.errorsById.get(row.id) || []).join(', ');
      return `<tr>
        <td>${row.personHC || '&mdash;'}</td>
        <td>${isoDateKey(row.date) || '&mdash;'}</td>
        <td>${row.startTime || '&mdash;'}</td>
        <td>${row.stopTime || '&mdash;'}</td>
        <td>${row.payrollAttribute || '&mdash;'}</td>
        <td>${hoursBetween(row.startTime, row.stopTime).toFixed(2)}</td>
        <td>${errs || '&mdash;'}</td>
      </tr>`;
    }).join('');
    win.document.write(`<!doctype html><html><head><title>NICE Timesheet</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
      h1{font-size:20px;margin:0 0 8px}
      p{font-size:12px;color:#475569}
      table{width:100%;border-collapse:collapse;margin-top:16px}
      th,td{border:1px solid #cbd5e1;padding:8px;font-size:12px;text-align:left}
      th{background:#f1f5f9}
    </style></head><body>
      <h1>NICE Time Sheet Workbench</h1>
      <p>Generated ${new Date().toLocaleString()}</p>
      <table><thead><tr><th>Person</th><th>Date</th><th>Start</th><th>Stop</th><th>Payroll Type</th><th>Hours</th><th>Validation</th></tr></thead><tbody>${rowsHtml}</tbody></table>
    </body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <section className="rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50 via-sky-50 to-teal-100/70 p-4 sm:p-6 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          <p className="mt-1 text-sm text-slate-600">Edit rows quickly, validate overlaps, and calculate overtime before upload.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={addRow} className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-800">
            <Plus className="h-4 w-4" /> Add row
          </button>
          <button type="button" onClick={handleExportCsv} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button type="button" onClick={handleExportPdf} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <FileText className="h-4 w-4" /> PDF
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-xs uppercase tracking-wide text-slate-500">Rows</p>
          <p className="mt-1 text-2xl font-semibold text-slate-800">{rows.length}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs uppercase tracking-wide text-amber-700">Validation issues</p>
          <p className="mt-1 text-2xl font-semibold text-amber-900">{validation.errorsById.size}</p>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs uppercase tracking-wide text-emerald-700">Valid rows</p>
          <p className="mt-1 text-2xl font-semibold text-emerald-900">{validation.validRows.length}</p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-[980px] w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-slate-700">Person</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700">Date</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700">Start</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700">Stop</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700">Payroll type</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700">Hours</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700">Validation</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.map((row, idx) => {
              const errors = validation.errorsById.get(row.id) || [];
              const rowKeyDown = (e) => {
                if (e.key === 'Enter' && idx === rows.length - 1) {
                  e.preventDefault();
                  addRow();
                }
              };
              const dayHours = totals.byDay.get(`${row.personHC}__${isoDateKey(row.date)}`) || 0;
              return (
                <tr key={row.id} className={errors.length > 0 ? 'bg-rose-50/40' : 'hover:bg-slate-50'}>
                  <td className="px-3 py-2">
                    <input value={row.personHC} onChange={(e) => updateCell(row.id, 'personHC', e.target.value)} onKeyDown={rowKeyDown} className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5" placeholder="PersonHC" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="date" value={isoDateKey(row.date)} onChange={(e) => updateCell(row.id, 'date', e.target.value)} onKeyDown={rowKeyDown} className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="time" value={row.startTime} onChange={(e) => updateCell(row.id, 'startTime', e.target.value)} onKeyDown={rowKeyDown} className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="time" value={row.stopTime} onChange={(e) => updateCell(row.id, 'stopTime', e.target.value)} onKeyDown={rowKeyDown} className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5" />
                  </td>
                  <td className="px-3 py-2">
                    <select value={row.payrollAttribute} onChange={(e) => updateCell(row.id, 'payrollAttribute', e.target.value)} onKeyDown={rowKeyDown} className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5">
                      <option value="Regular Hours US">Regular Hours US</option>
                      <option value="Meal Break US">Meal Break US</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-800">
                    {formatHours(hoursBetween(row.startTime, row.stopTime))}
                    <div className="text-[11px] text-slate-500">Day total: {formatHours(dayHours)}</div>
                  </td>
                  <td className="px-3 py-2">
                    {errors.length > 0 ? <span className="text-xs text-rose-700">{errors.join(', ')}</span> : <span className="text-xs text-emerald-700">OK</span>}
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => removeRow(row.id)} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-sm text-slate-500">No rows yet. Add one or load from NICE preview.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        <p className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-800"><CalendarDays className="h-4 w-4" /> Weekly totals and overtime</p>
        {totals.weeklySummary.length === 0 ? (
          <p className="text-sm text-slate-500">No valid entries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[640px] w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-3">Person</th>
                  <th className="py-2 pr-3">Week start</th>
                  <th className="py-2 pr-3">Total</th>
                  <th className="py-2 pr-3">Overtime</th>
                </tr>
              </thead>
              <tbody>
                {totals.weeklySummary.map((wk) => (
                  <tr key={`${wk.personHC}-${wk.weekStart}`} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium text-slate-800">{wk.personHC}</td>
                    <td className="py-2 pr-3 text-slate-700">{wk.weekStart}</td>
                    <td className="py-2 pr-3 text-slate-700 inline-flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" /> {formatHours(wk.hours)}</td>
                    <td className={`py-2 pr-3 font-medium ${wk.overtime > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{formatHours(wk.overtime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/** Help: backend, API URLs, and Accutime/NICE time sheet usage (content previously on the Accutime upload page). */
function TimeSheetsHelpView({ apiBase, apiBaseSource }) {
  const atsUploadPath = '/api/upload-time-sheet';
  const niceUploadPath = '/api/upload-nice-time-sheet';
  const configuredFullUrl = apiBase ? `${String(apiBase).replace(/\/$/, '')}${atsUploadPath}` : null;
  const niceConfiguredFullUrl = apiBase ? `${String(apiBase).replace(/\/$/, '')}${niceUploadPath}` : null;
  return (
    <div className="max-w-3xl space-y-6">
      <div className="card-panel p-6">
        <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <CircleHelp className="h-6 w-6 text-cyan-600 shrink-0" />
          Help — Time sheets
        </h1>
        <p className="mt-1 text-sm text-slate-600">How the app talks to the HCM Reporting Tool backend and what each upload tab expects.</p>
      </div>

      <section className="card-panel p-6 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Accutime tab in this app</h2>
        <p className="text-sm text-slate-700">
          Accutime accepts <strong>Start/Stop</strong> per row, or <strong>punch</strong> rows (<strong>Timestamp Wall Time</strong> + <strong>Punch Type</strong> In/Out).{' '}
          <strong>Review mapping</strong> shows the paired segments sent to Oracle. Use <strong>NICE Time Sheet</strong> for NICE EventState columns.
        </p>
        <p className="text-sm text-slate-700">
          Use the Accutime tab for either <strong>Date + StartTime + StopTime</strong>, or <strong>Timestamp Wall Time</strong> where each value is the punch instant —{' '}
          <strong>In</strong> = clock-in (segment start), <strong>Out</strong> = clock-out (segment end). Pairing matches In to Out by person and day.{' '}
          <strong>Upload</strong> uses the same pairing as <strong>Review</strong>.
        </p>
      </section>

      <section className="card-panel p-6 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">NICE Time Sheet upload API</h2>
        <p className="text-sm text-slate-700">
          Upload endpoint (multipart field <code className="text-xs">file</code>):
        </p>
        <p className="text-sm font-mono text-slate-800 break-all rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          http://localhost:3002{niceUploadPath}
        </p>
        {!niceConfiguredFullUrl && (
          <p className="text-xs text-slate-600">
            API requests from this build use <span className="font-mono">POST {niceUploadPath}</span> on the same origin as the page unless <code className="text-xs">REACT_APP_API_URL</code> is set.
          </p>
        )}
        <div className="pt-2 border-t border-slate-100 space-y-2">
          <p className="text-sm text-slate-700">
            Use the <strong>NICE (EventTime/EventState)</strong> tab for NICE format (<strong>PersonHC</strong>, <strong>EventTime</strong>, <strong>EventState</strong>).{' '}
            EventTime must be <strong>24-hour</strong> in Excel. Punch pairing: <strong>LoggedIn</strong>+<strong>LoggedOut</strong> → one row.{' '}
            <strong>Upload</strong> uses the same server and parser as <strong>Review</strong> — segment count should match.
          </p>
        </div>
      </section>
    </div>
  );
}

function AtsUploadHeaderContext({ user, connectionsList = [], selectedConnectionId }) {
  const activeConnectionId = !user && connectionsList?.length > 0
    ? (connectionsList.some((c) => c.id === selectedConnectionId) ? selectedConnectionId : connectionsList[0]?.id)
    : null;
  const activeConnection = connectionsList?.find((c) => c.id === activeConnectionId);

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-sm font-semibold text-slate-800 mb-2">Punch export (Timestamp Wall Time)</p>
      <p className="text-sm text-slate-700">
        Each row is one punch. <strong>Timestamp Wall Time</strong> is the instant of that punch; <strong>Punch Type</strong> says whether it is a <strong>start</strong> (In) or <strong>stop</strong> (Out) for pairing. We sort by time, then pair In→Out into work segments.
      </p>

      <div className="mt-4 rounded-lg border border-cyan-200 bg-cyan-50/50 p-4">
        <p className="text-sm font-semibold text-cyan-900 mb-1">Upload destination</p>
        <p className="text-xs text-cyan-800/90 mb-2">Time data will be sent to Oracle HCM at:</p>
        {user ? (
          <p className="text-sm font-medium text-slate-800 break-all" title={user.instanceUrl || 'Oracle instance from your login'}>
            {user.instanceUrl || 'Oracle instance (from your sign-in)'}
          </p>
        ) : connectionsList?.length > 0 ? (
          <>
            {connectionsList.length === 1 ? (
              <p className="text-sm font-medium text-slate-800 break-all" title={activeConnection?.application_hcm_url}>
                {activeConnection?.application_hcm_url || '—'}
              </p>
            ) : (
              <select
                value={activeConnectionId || ''}
                onChange={() => {}}
                disabled
                className="w-full max-w-2xl rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 disabled:opacity-100"
              >
                {connectionsList.map((conn) => (
                  <option key={conn.id} value={conn.id}>
                    {conn.connection_name || 'Unnamed'} — {conn.application_hcm_url || ''}
                  </option>
                ))}
              </select>
            )}
            {activeConnection && (
              <p className="text-xs text-slate-600 mt-1">
                Connection: {activeConnection.connection_name || 'Unnamed'}
                {activeConnection.username && ` · User: ${activeConnection.username}`}
              </p>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function NiceUploadSideContext({ user, connectionsList = [], selectedConnectionId }) {
  const activeConnectionId = !user && connectionsList?.length > 0
    ? (connectionsList.some((c) => c.id === selectedConnectionId) ? selectedConnectionId : connectionsList[0]?.id)
    : null;
  const activeConnection = connectionsList?.find((c) => c.id === activeConnectionId);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-cyan-200 bg-cyan-50/50 p-4">
        <p className="text-sm font-semibold text-cyan-900 mb-1">Upload destination</p>
        <p className="text-xs text-cyan-800/90 mb-2">Time data will be sent to Oracle HCM at:</p>
        {user ? (
          <p className="text-sm font-medium text-slate-800 break-all" title={user.instanceUrl || 'Oracle instance from your login'}>
            {user.instanceUrl || 'Oracle instance (from your sign-in)'}
          </p>
        ) : connectionsList?.length > 0 ? (
          <>
            {connectionsList.length === 1 ? (
              <p className="text-sm font-medium text-slate-800 break-all" title={activeConnection?.application_hcm_url}>
                {activeConnection?.application_hcm_url || '—'}
              </p>
            ) : (
              <select
                value={activeConnectionId || ''}
                onChange={() => {}}
                disabled
                className="w-full max-w-2xl rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 disabled:opacity-100"
              >
                {connectionsList.map((conn) => (
                  <option key={conn.id} value={conn.id}>
                    {conn.connection_name || 'Unnamed'} — {conn.application_hcm_url || ''}
                  </option>
                ))}
              </select>
            )}
            {activeConnection && (
              <p className="text-xs text-slate-600 mt-1">
                Connection: {activeConnection.connection_name || 'Unnamed'}
                {activeConnection.username && ` · User: ${activeConnection.username}`}
              </p>
            )}
          </>
        ) : null}
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-semibold text-slate-800 mb-2">EventState → start/stop and PayrollTimeType</p>
        <ul className="text-sm text-slate-700 space-y-1 font-mono">
          <li><strong>LoggedIn</strong> → StartTime, <strong>LoggedOut</strong> → StopTime → <span className="text-slate-700">Regular Hours US</span></li>
          <li><strong>LunchOut</strong> → StartTime, <strong>LunchIn</strong> → StopTime → <span className="text-slate-700">Meal Break US</span></li>
        </ul>
      </div>
    </div>
  );
}

function TimeSheetUploadView({
  user,
  uploadFile,
  setUploadFile,
  uploading,
  setUploading,
  uploadResult,
  setUploadResult,
  uploadError,
  setUploadError,
  uploadApiUrl = '/api/upload-time-sheet',
  title = 'Accutime Time Sheet Upload',
  expectedColumns = 'PersonHCM, Date, StartTime, StopTime (optional: Agent_No, EventState).',
  onUploadComplete,
  showReviewStep = false,
  previewApiUrl = '/api/nice-time-sheet-preview',
  apiBaseUrl = '',
  hideOracleLoginPrompt = false,
  onRequestOracleLogin,
  connectionsList = [],
  selectedConnectionId = null,
  onSelectConnectionId,
  showPunchExportInfo = true,
  showUploadDestination = true,
}) {
  const hasCredentials = user || (connectionsList && connectionsList.length > 0);
  const isNiceReview = showReviewStep && String(uploadApiUrl || '').includes('nice');
  const activeConnectionId = !user && connectionsList?.length > 0
    ? (connectionsList.some((c) => c.id === selectedConnectionId) ? selectedConnectionId : connectionsList[0]?.id)
    : null;
  const activeConnection = connectionsList?.find((c) => c.id === activeConnectionId);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const fileInputRef = useRef(null);
  const isJobInProgress =
    !!currentJobId &&
    !['completed', 'completed_with_errors', 'failed'].includes(String(uploadResult?.status || ''));

  const postFormWithNetworkFallback = useCallback(async (apiPath, formData) => {
    const attempted = [];
    const targets = [];
    const absolute = `${API_BASE}${apiPath}`;
    if (API_BASE) targets.push(absolute);
    // Fallback to relative path so CRA dev proxy can still route to backend.
    targets.push(apiPath);

    let lastNetworkError = null;
    for (const target of targets) {
      if (attempted.includes(target)) continue;
      attempted.push(target);
      try {
        const res = await fetch(target, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        return { res, attemptedUrl: target, attemptedUrls: attempted };
      } catch (err) {
        const msg = err?.message || '';
        const isNetworkError =
          msg === 'Failed to fetch' ||
          /network|fetch|ECONNREFUSED|Load failed|NetworkError/i.test(msg);
        if (!isNetworkError) throw err;
        lastNetworkError = err;
      }
    }
    const finalErr = new Error(lastNetworkError?.message || 'Failed to fetch');
    finalErr.attemptedUrls = attempted;
    throw finalErr;
  }, []);

  const fetchJobStatus = useCallback(async (jobId = currentJobId) => {
    if (!jobId) return;
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || `Job status failed (${res.status})`);
      }
      setUploadResult(data);
      if (data.status === 'failed' && data.error) {
        setUploadError(data.error);
      } else if (data.status === 'completed_with_errors' && data.message) {
        setUploadError(data.message);
      } else if (data.status === 'completed') {
        setUploadError(null);
      }
    } catch (err) {
      setUploadError((prev) => prev || err.message || 'Failed to refresh job status');
    }
  }, [currentJobId, setUploadResult, setUploadError]);

  const clearProcessLogs = useCallback(() => {
    setUploadResult(null);
    setUploadError(null);
    setCurrentJobId(null);
    setUploadProgress(0);
    setUploading(false);
  }, [setUploadResult, setUploadError, setUploading]);

  useEffect(() => {
    if (!currentJobId) return undefined;
    if (uploadResult?.status && ['completed', 'completed_with_errors', 'failed'].includes(uploadResult.status)) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      fetchJobStatus(currentJobId);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [currentJobId, uploadResult?.status, fetchJobStatus]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setUploadFile(file);
      setUploadResult(null);
      setUploadError(null);
      setPreviewData(null);
      setPreviewError(null);
      setUploadProgress(0);
      setCurrentJobId(null);
    } else {
      setUploadError('Please drop an Excel file (.xlsx or .xls).');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleFileChange = (e) => {
    const file = e.target?.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadResult(null);
      setUploadError(null);
      setPreviewData(null);
      setPreviewError(null);
      setUploadProgress(0);
      setCurrentJobId(null);
    }
  };

  const handleReviewMapping = async () => {
    if (!uploadFile || !previewApiUrl) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (activeConnectionId) {
        formData.append('connectionId', activeConnectionId);
      }
      const { res } = await postFormWithNetworkFallback(previewApiUrl, formData);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = res.status === 404
          ? 'Review mapping is not available (preview endpoint returned 404). Ensure the HCM Reporting Tool backend is running (start-server-new-window.bat) on port 3002 and set REACT_APP_API_URL=http://localhost:3002 in client/.env if needed, then restart the client. You can still upload without reviewing.'
          : (data.error || data.message || `Request failed (${res.status})`);
        setPreviewError(msg);
        return;
      }
      if (!data.success || !data.previews) {
        setPreviewError(data.error || data.message || 'Preview returned unexpected format.');
        return;
      }
      setPreviewData(data);
    } catch (err) {
      const attemptedUrls = Array.isArray(err?.attemptedUrls) ? err.attemptedUrls : [];
      const detail = attemptedUrls.length ? ` Attempted: ${attemptedUrls.join(' , ')}` : '';
      setPreviewError((err.message || 'Preview failed. Ensure the backend is running and has /api/nice-time-sheet-preview.') + detail);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClearMapping = () => {
    setPreviewData(null);
    setPreviewError(null);
  };

  const handleUpload = async () => {
    if (!uploadFile || !hasCredentials) return;
    setUploading(true);
    setUploadError(null);
    setUploadResult(null);
    setCurrentJobId(null);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (activeConnectionId) {
        formData.append('connectionId', activeConnectionId);
      }
      const { res } = await postFormWithNetworkFallback(uploadApiUrl, formData);
      const rawText = await res.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch (_) {
        data = {};
      }
      setUploadProgress(100);
      if (!res.ok) {
        if (/^\s*<!DOCTYPE|^\s*<html/i.test(rawText)) {
          data._html404 = true;
          data._rawBody = rawText.slice(0, 500);
          const reactPort =
            typeof window !== 'undefined' && window.location?.port ? window.location.port : '3001';
          const onReactDevPort = ['3000', '3001'].includes(reactPort);
          data.message = onReactDevPort
            ? `You're viewing the app at http://localhost:${reactPort} — that's the frontend. So API requests to :${reactPort} can hit the React app and return HTML. The backend uses port 3002. Start the backend (e.g. dev-local.bat or start-server-new-window.bat), then open http://localhost:${reactPort}.`
            : 'The server on port 3002 returned an HTML 404 page. Start the HCM Reporting Tool with start-server-new-window.bat in c:\\Users\\lmann\\hcm-reporting-tool.';
          setUploadError(data.message);
        } else {
          let msg =
            data.message ||
            data.error ||
            (res.status === 403 ? '403 Forbidden. See the message and Oracle response below.' : `Request failed (${res.status})`);
          msg = typeof msg === 'string' && msg.includes('timeEvents API')
            ? msg.replace(/timeEvents API/gi, 'timeRecordEventRequests and child endpoints')
            : msg;
          if (res.status === 401 && (data.code === 'ORACLE_CREDENTIALS_REQUIRED' || data.code === 'TOOL_AUTH_REQUIRED')) {
            msg += '\n\nSession may have expired. Try logging out and back in, then sign in to Oracle (or add a Connection in Connections).';
          }
          const withHint = data.hint ? `${msg}\n\n${data.hint}` : msg;
          setUploadError(withHint);
        }
        setUploadResult(data);
        return;
      }
      setUploadResult(data);
      if (data.jobId) {
        setCurrentJobId(data.jobId);
        setUploadProgress(30);
        await fetchJobStatus(data.jobId);
      }
      onUploadComplete?.();
    } catch (err) {
      setUploadProgress(100);
      const attemptedUrls = Array.isArray(err?.attemptedUrls) ? err.attemptedUrls : [];
      const detail = attemptedUrls.length ? ` Attempted: ${attemptedUrls.join(' , ')}` : '';
      const baseMessage = err.message || 'Upload failed';
      const looksLikeNetwork =
        baseMessage === 'Failed to fetch' ||
        /network|fetch|ECONNREFUSED|Load failed|NetworkError/i.test(baseMessage);
      const hint = looksLikeNetwork
        ? 'Unable to reach API for upload. Confirm backend is running on port 3002 and open http://localhost:3002/api/health.'
        : '';
      const finalMessage = [baseMessage, detail, hint].filter(Boolean).join('\n');
      setUploadError(finalMessage);
      setUploadResult({ _networkError: true, message: finalMessage });
    } finally {
      setUploading(false);
    }
  };

  const succeededCount = uploadResult?.successful ?? uploadResult?.succeeded ?? 0;
  const failedCount = uploadResult?.failed ?? 0;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="card-panel p-6">
        <h1 className="text-xl font-semibold text-slate-800">{title}</h1>
        <p className="mt-1 text-sm text-slate-600">
          Excel columns: {expectedColumns}
          Rows are sent to Oracle HCM. Sign in to upload.
        </p>
      {showReviewStep && !isNiceReview && showPunchExportInfo && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-800 mb-2">Punch export (Timestamp Wall Time)</p>
          <p className="text-sm text-slate-700">
            Each row is one punch. <strong>Timestamp Wall Time</strong> is the instant of that punch; <strong>Punch Type</strong> says whether it is a <strong>start</strong> (In) or <strong>stop</strong> (Out) for pairing. We sort by time, then pair In→Out into work segments.
          </p>
        </div>
      )}

      {!hasCredentials && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Log in to Oracle or add a connection to upload time sheets.
          {onRequestOracleLogin && (
            <button
              type="button"
              onClick={onRequestOracleLogin}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
            >
              <LogIn className="h-4 w-4" />
              Oracle log in
            </button>
          )}
        </div>
      )}

      {hasCredentials && showUploadDestination && (
        <div className="mt-4 rounded-lg border-2 border-cyan-200 bg-cyan-50/50 p-4">
          <p className="text-sm font-semibold text-cyan-900 mb-1">Upload destination</p>
          <p className="text-xs text-cyan-800/90 mb-2">Time data will be sent to Oracle HCM at:</p>
          {user ? (
            <p className="text-sm font-medium text-slate-800 break-all" title={user.instanceUrl || 'Oracle instance from your login'}>
              {user.instanceUrl || 'Oracle instance (from your sign-in)'}
            </p>
          ) : connectionsList?.length > 0 ? (
            <>
              {connectionsList.length === 1 ? (
                <p className="text-sm font-medium text-slate-800 break-all" title={activeConnection?.application_hcm_url}>
                  {activeConnection?.application_hcm_url || '—'}
                </p>
              ) : (
                <select
                  value={activeConnectionId || ''}
                  onChange={(e) => onSelectConnectionId?.(e.target.value || null)}
                  className="w-full max-w-2xl rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                >
                  {connectionsList.map((conn) => (
                    <option key={conn.id} value={conn.id}>
                      {conn.connection_name || 'Unnamed'} — {conn.application_hcm_url || ''}
                    </option>
                  ))}
                </select>
              )}
              {activeConnection && (
                <p className="text-xs text-slate-600 mt-1">
                  Connection: {activeConnection.connection_name || 'Unnamed'}
                  {activeConnection.username && ` · User: ${activeConnection.username}`}
                </p>
              )}
            </>
          ) : null}
        </div>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`mt-4 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
          dragOver ? 'border-sky-300 bg-sky-100/70' : 'border-sky-200 bg-sky-50/70'
        } ${uploading ? 'pointer-events-none opacity-80' : ''}`}
      >
        <FileSpreadsheet className="mx-auto h-12 w-12 text-slate-400" />
        <p className="mt-2 text-slate-600">Drag and drop your Excel file here, or</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          className="mt-2 hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-slate-700 disabled:opacity-50"
        >
          Browse
        </button>
        {uploadFile && (
          <p className="mt-3 text-sm font-medium text-slate-800">{uploadFile.name}</p>
        )}
      </div>

      {/* NICE Review step: Review mapping button and preview table */}
      {showReviewStep && uploadFile && (
        <>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-800 mb-2">Review mapping before upload</p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={previewLoading}
                onClick={handleReviewMapping}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                {previewLoading ? 'Loading…' : 'Review mapping'}
              </button>
              <button
                type="button"
                disabled={previewLoading || (!previewData && !previewError)}
                onClick={handleClearMapping}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                Clear mapping
              </button>
            </div>
          </div>
          {previewError && (
            <div className={`flex items-start gap-2 rounded-lg p-4 text-sm ${
              previewError.includes('optional') || previewError.includes('not available')
                ? 'bg-amber-50 text-amber-800 border border-amber-200'
                : 'bg-red-50 text-red-800'
            }`}>
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="min-w-0 flex-1">{previewError}</div>
            </div>
          )}
          {previewData && previewData.previews && previewData.previews.length === 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              No rows to preview. Ensure your Excel file has a header row and at least one data row with Agent_No, Date, EventTime, EventState, and HCM_Pers (or PersonHCM).
            </div>
          )}
          {previewData && previewData.previews && previewData.previews.length > 0 && (
            <>
              {previewData.timeCards && previewData.timeCards.length > 0 && (
                <div className="card-panel overflow-hidden mb-6">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">
                    {isNiceReview ? 'Time cards that will be created' : 'Rows that will be created'} ({previewData.timeCards.length} row{previewData.timeCards.length !== 1 ? 's' : ''})
                  </div>
                  {previewData.timeCards.some((c) => c.exception) && (
                    <p className="px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-slate-200">
                      Rows with a <strong>missing punch</strong> (see Exception column) are sent to Oracle with a comment so you can see them in Time and Labor.
                    </p>
                  )}
                  <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th scope="col" className="px-3 py-2.5 text-left font-semibold text-slate-700">#</th>
                          {isNiceReview && (
                            <th scope="col" className="px-3 py-2.5 text-left font-semibold text-slate-700">External ID</th>
                          )}
                          <th scope="col" className="px-3 py-2.5 text-left font-semibold text-slate-700">
                            {isNiceReview ? 'Person number' : 'Person number (Employee ID)'}
                          </th>
                          <th scope="col" className="px-3 py-2.5 text-left font-semibold text-slate-700">Date</th>
                          <th scope="col" className="px-3 py-2.5 text-left font-semibold text-slate-700">Start time</th>
                          <th scope="col" className="px-3 py-2.5 text-left font-semibold text-slate-700">Stop time</th>
                          <th scope="col" className="px-3 py-2.5 text-left font-semibold text-slate-700">Payroll attribute</th>
                          <th scope="col" className="px-3 py-2.5 text-left font-semibold text-slate-700">Exception</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {previewData.timeCards.map((card) => (
                          <tr
                            key={card.index}
                            className={card.exception ? 'bg-amber-50/50' : 'hover:bg-slate-50'}
                          >
                            <td className="px-3 py-2 font-mono text-slate-600">{card.index}</td>
                            {isNiceReview && (
                              <td className="px-3 py-2 font-mono text-slate-700">{card.externalIdentifier || '—'}</td>
                            )}
                            <td className="px-3 py-2 font-medium text-slate-800">{card.personNumber || card.personHC || '—'}</td>
                            <td className="px-3 py-2 text-slate-700">{card.date}</td>
                            <td className="px-3 py-2 font-mono text-slate-700">{card.startTime || '—'}</td>
                            <td className="px-3 py-2 font-mono text-slate-700">{card.stopTime || '—'}</td>
                            <td className="px-3 py-2 text-slate-700">{card.payrollAttribute}</td>
                            <td className="px-3 py-2 text-amber-700 italic">{card.exception || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
          </>
          )}

        </>
      )}

      {showReviewStep && (
        <TimeSheetWorkbench previewData={previewData} title={isNiceReview ? 'NICE Time Sheet Workbench' : 'Accutime Time Sheet Workbench'} />
      )}

      {/* Progress bar */}
      {(uploading || (uploadProgress === 100 && uploadResult)) && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-slate-700">
              {uploading ? 'Uploading and processing rows…' : 'Complete'}
            </span>
            {!uploading && uploadResult && (
              <span className="text-slate-500">
                {succeededCount} succeeded, {failedCount} failed
              </span>
            )}
          </div>
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className={`h-full rounded-full bg-slate-600 transition-all duration-300 ${
                uploading ? 'animate-pulse w-full' : ''
              }`}
              style={{
                width: uploading ? '100%' : `${uploadProgress}%`,
              }}
            />
          </div>
          {uploading && (
            <p className="mt-2 text-xs text-slate-500">Sending rows to Oracle…</p>
          )}
        </div>
      )}

      {uploadError && (
        <>
          <div className="flex flex-col gap-3 rounded-lg bg-red-50 p-4 text-red-800 text-sm border-2 border-red-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="min-w-0 flex-1 whitespace-pre-line break-words">{uploadError}</div>
            </div>
          </div>
          <div className="mt-4 p-4 rounded-lg border-2 border-amber-400 bg-amber-50">
            <p className="font-sans text-base font-bold text-amber-900 mb-2 uppercase tracking-wide">Oracle response (exact) — scroll below to see</p>
            {uploadResult?.oracleResponseBody != null ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    const text = typeof uploadResult.oracleResponseBody === 'object' ? JSON.stringify(uploadResult.oracleResponseBody, null, 2) : String(uploadResult.oracleResponseBody);
                    navigator.clipboard?.writeText(text).then(() => alert('Copied to clipboard.'));
                  }}
                  className="mb-2 text-xs font-medium px-3 py-1.5 rounded bg-amber-200 text-amber-900 hover:bg-amber-300"
                >
                  Copy to clipboard
                </button>
                <pre className="whitespace-pre-wrap break-all max-h-72 overflow-y-auto p-3 bg-white rounded border-2 border-amber-300 text-xs">{typeof uploadResult.oracleResponseBody === 'object' ? JSON.stringify(uploadResult.oracleResponseBody, null, 2) : String(uploadResult.oracleResponseBody)}</pre>
              </>
            ) : (
              <p className="text-amber-800 italic">No response body from server. Check network tab or server logs.</p>
            )}
          </div>
        </>
      )}

      <button
        type="button"
        disabled={!hasCredentials || !uploadFile || uploading || isJobInProgress}
        onClick={handleUpload}
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-400 disabled:text-slate-100 disabled:shadow-none disabled:opacity-100"
      >
        {uploading || isJobInProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {uploading || isJobInProgress ? 'Processing…' : 'Upload to Oracle'}
      </button>

      {uploadResult && (uploadResult.results?.length ?? 0) > 0 && (
        <>
          {uploadResult.message && uploadResult.failed > 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-4 text-amber-900 text-sm border border-amber-200">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1 whitespace-pre-line break-words">
                <p className="font-medium">{uploadResult.message}</p>
                {uploadResult.hint && <p className="mt-2 text-amber-700">{uploadResult.hint}</p>}
                {uploadResult.payloadSentToOracle != null && (
                  <div className="mt-3 rounded border border-amber-200 bg-white p-3 font-mono text-xs overflow-x-auto">
                    <p className="font-sans font-medium text-amber-800 mb-1">JSON sent to Oracle (NICE / timeRecordEventRequests):</p>
                    {uploadResult.requestUrl && <p className="mb-1"><strong>Request URL:</strong> {uploadResult.requestUrl}</p>}
                    {uploadResult.note && <p className="mb-1 text-amber-700 font-sans">{uploadResult.note}</p>}
                    <pre className="whitespace-pre-wrap break-all mt-1">{JSON.stringify(uploadResult.payloadSentToOracle, null, 2)}</pre>
                    {uploadResult.oracleResponseBody != null && (
                      <>
                        <p className="font-sans font-medium text-amber-800 mt-2 mb-1">Oracle response (exact):</p>
                        <pre className="whitespace-pre-wrap break-all bg-red-50 p-2 rounded">{typeof uploadResult.oracleResponseBody === 'object' ? JSON.stringify(uploadResult.oracleResponseBody, null, 2) : String(uploadResult.oracleResponseBody)}</pre>
                      </>
                    )}
                  </div>
                )}
                {uploadResult.debug403 && (
                  <div className="mt-3 rounded border border-amber-200 bg-white p-3 font-mono text-xs overflow-x-auto">
                    <p className="font-sans font-medium text-amber-800 mb-1">Diagnostics (for Oracle admin / support):</p>
                    <p><strong>Request URL:</strong> {uploadResult.debug403.requestUrl}</p>
                    <p className="mt-1"><strong>Sample payload:</strong> {JSON.stringify(uploadResult.debug403.samplePayload)}</p>
                    <p className="mt-1"><strong>Oracle response:</strong> {typeof uploadResult.debug403.oracleResponseBody === 'object' ? JSON.stringify(uploadResult.debug403.oracleResponseBody) : String(uploadResult.debug403.oracleResponseBody)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          <UploadStatus
            result={uploadResult}
            apiBaseUrl={apiBaseUrl}
            uploadApiUrl={uploadApiUrl}
            onRefreshJobStatus={currentJobId ? () => fetchJobStatus(currentJobId) : undefined}
            onClearProcessLogs={clearProcessLogs}
          />
        </>
      )}

      {uploadResult && (uploadResult.results?.length ?? 0) === 0 && (
        uploadResult.debug ||
        (uploadResult.message &&
          !(uploadResult.jobId && uploadResult.status === 'completed' && (uploadResult.failed ?? 0) === 0))
      ) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {uploadResult.message &&
            !(uploadResult.jobId && uploadResult.status === 'completed' && (uploadResult.failed ?? 0) === 0) && (
              <p className="font-medium">{uploadResult.message}</p>
            )}
          {uploadResult.debug && (
            <div className="mt-3 space-y-1 font-mono text-xs">
              <p>Sheet: {uploadResult.debug.sheetName} · Rows read: {uploadResult.debug.rowCount}</p>
              <p>Expected columns: {uploadResult.debug.expectedColumns}</p>
              {uploadResult.debug.firstRowColumnNames?.length > 0 && (
                <p>Your column names: {uploadResult.debug.firstRowColumnNames.join(', ')}</p>
              )}
              <p className="mt-2 text-amber-700">{uploadResult.debug.hint}</p>
            </div>
          )}
        </div>
      )}
    </div>
    </div>
  );
}

function MainHeaderClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const time = now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  const date = now.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  return (
    <div
      className="flex items-center gap-2.5 rounded-lg border border-white/70 bg-white/80 px-3 py-1.5 shadow-sm backdrop-blur-sm"
      title={now.toString()}
    >
      <Clock3 className="h-4 w-4 shrink-0 text-cyan-800" aria-hidden />
      <div className="min-w-0 text-left">
        <p className="font-mono text-base font-semibold tabular-nums tracking-tight text-slate-900 leading-none">{time}</p>
        <p className="mt-0.5 text-[11px] text-slate-700">{date}</p>
      </div>
    </div>
  );
}

export default function App() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState('SQL Recipes');
  const [favorites, setFavorites] = useState(() => loadFavorites());
  const [runModalReport, setRunModalReport] = useState(null);
  const [runModalSqlRecipe, setRunModalSqlRecipe] = useState(null);
  const [cardFormat, setCardFormat] = useState('pdf');
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [view, setView] = useState('reports'); // 'reports' | 'payrollHoursReport' | 'timeCardsList' | 'upload' | 'niceUpload' | 'hcmTimeCardDelete' | 'help' | 'connections' | 'reportHistory'
  const [showProductLogin, setShowProductLogin] = useState(false);
  const [timeCardsListPrefill, setTimeCardsListPrefill] = useState(null);
  const [hcmTimeCardDeletePrefill, setHcmTimeCardDeletePrefill] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [niceUploadFile, setNiceUploadFile] = useState(null);
  const [niceUploading, setNiceUploading] = useState(false);
  const [niceUploadResult, setNiceUploadResult] = useState(null);
  const [niceUploadError, setNiceUploadError] = useState(null);
  const [toolUser, setToolUser] = useState(null);
  const [connectionsList, setConnectionsList] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [openNewConnectionModal, setOpenNewConnectionModal] = useState(false);
  const [reportHistoryFolder, setReportHistoryFolder] = useState('logs');
  const [reportHistoryRefresh, setReportHistoryRefresh] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => loadSidebarCollapsed());

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      saveSidebarCollapsed(next);
      return next;
    });
  }, []);

  useEffect(() => {
    API.get('/auth/me', { validateStatus: (s) => s === 200 || s === 401 })
      .then((res) => {
        if (res.status === 401) {
          setToolUser(null);
          setUser(null);
          return;
        }
        setToolUser(res.data.toolUser || null);
        setUser(res.data.oracleUser || null);
      })
      .catch(() => {
        setToolUser(null);
        setUser(null);
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    /** BIP-only load: use /api/catalog and then local seeded fallback in UI if empty. */
    const fetchCatalog = () =>
      API.get('/api/catalog', { validateStatus: () => true })
        .then((res) => {
          if (res.status === 200) {
            const raw = res.data.reports || [];
            setReports(raw.filter((r) => isXdoReport(r.path)));
            setError(null);
            return;
          }
          const fallbackMsg =
            res.status === 503
              ? 'Not signed in. Please Connect'
              : res.data?.message || res.data?.error || `Catalog request failed (${res.status}).`;
          setReports([]);
          setError(fallbackMsg);
        })
        .catch((err) => {
          const msg = err.response?.data?.message || err.message;
          setError(msg);
          setReports([]);
        })
        .finally(() => setLoading(false));

    fetchCatalog();
  }, [user]);

  const refreshConnections = useCallback(() => {
    API.get('/api/connections', { validateStatus: (s) => s === 200 || s === 401 })
      .then((res) => {
        if (res.status === 401) return;
        const list = Array.isArray(res.data?.connections) ? res.data.connections : [];
        setConnectionsList(list);
      })
      .catch(() => setConnectionsList([]));
  }, []);

  useEffect(() => {
    if (toolUser) refreshConnections();
  }, [toolUser, refreshConnections]);

  const liveTimeReports = useMemo(() => reports.filter((r) => isTimeRelatedReport(r)), [reports]);
  const timeReports = useMemo(() => {
    const merged = new Map();
    for (const r of timeAbsenceReportsSeed) {
      if (isRunnableSqlRecipe(r)) merged.set(r.path, r);
    }
    for (const r of liveTimeReports) {
      if (isRunnableSqlRecipe(r)) merged.set(r.path, r);
    }
    return Array.from(merged.values());
  }, [liveTimeReports]);
  const categories = useMemo(() => getCategories(timeReports), [timeReports]);
  const filteredReports = useMemo(() => filterReports(timeReports, category), [timeReports, category]);
  const activeConnection = useMemo(() => {
    if (!connectionsList?.length) return null;
    const id =
      (selectedConnectionId && connectionsList.some((c) => c.id === selectedConnectionId)
        ? selectedConnectionId
        : null) || connectionsList[0]?.id;
    return connectionsList.find((c) => c.id === id) || null;
  }, [connectionsList, selectedConnectionId]);

  useEffect(() => {
    if (!categories.length) return;
    if (!categories.includes(category)) {
      setCategory(categories.includes('SQL Recipes') ? 'SQL Recipes' : categories[0]);
    }
  }, [categories, category]);

  if (!toolUser) {
    if (showProductLogin) {
      return (
        <ToolLogin
          onBack={() => setShowProductLogin(false)}
          onSuccess={(nextUser) => {
            setToolUser(nextUser);
          }}
        />
      );
    }
    return <LandingPage onSignIn={() => setShowProductLogin(true)} />;
  }

  const toggleFavorite = (path) => {
    setFavorites((prev) => {
      const set = new Set(prev);
      if (set.has(path)) set.delete(path);
      else set.add(path);
      const next = Array.from(set);
      saveFavorites(next);
      return next;
    });
  };

  return (
    <div className="flex h-screen flex-col bg-gradient-to-br from-slate-50 via-cyan-50/30 to-teal-50/40">
      {/* Full-width top bar — not affected by sidebar minimize */}
      <header className="tidesync-brand-strip flex shrink-0 items-center gap-3 border-b border-cyan-300/50 px-4 py-0.5 shadow-sm sm:gap-4 sm:px-5 sm:py-1">
        <div className="flex min-w-0 shrink-0 items-center sm:w-52">
          <img
            src="/tidesync-logo.png?v=2"
            alt="TideSync — Time punches to OTL"
            className="h-auto max-h-10 w-auto max-w-[11rem] object-contain object-left sm:max-h-[3.25rem] sm:max-w-none"
          />
        </div>
        <div className="flex min-w-0 flex-1 justify-center">
          <MainHeaderClock />
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-4">
          {user ? (
            <div className="hidden flex-col items-end gap-0.5 sm:flex">
              <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                <User className="h-4 w-4 text-slate-500" />
                <span className="text-slate-500 font-normal">Oracle</span>
              </span>
              {user.instanceUrl && (
                <span className="max-w-[180px] truncate text-xs text-slate-500" title={user.instanceUrl}>
                  {user.instanceUrl}
                </span>
              )}
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                <CheckCircle className="h-3 w-3 shrink-0" />
                Connected
              </span>
            </div>
          ) : null}
          {activeConnection ? (
            <span
              className="inline-flex max-w-[10rem] items-center gap-1.5 truncate rounded-lg border border-cyan-200/80 bg-white/70 px-2.5 py-1.5 text-sm font-medium text-slate-800 sm:max-w-[14rem]"
              title={activeConnection.application_hcm_url || activeConnection.connection_name}
            >
              <Link2 className="h-4 w-4 shrink-0 text-teal-700" aria-hidden />
              <span className="truncate">{activeConnection.connection_name || 'Unnamed'}</span>
            </span>
          ) : (
            <span className="text-sm text-slate-600">No connection</span>
          )}
          <button
            type="button"
            onClick={() => {
              API.post('/auth/logout').then(() => {
                setToolUser(null);
                setUser(null);
              }).catch(() => {
                setToolUser(null);
                setUser(null);
              });
            }}
            className="rounded-lg bg-[#FF4D5E] px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-[#e63e4f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF4D5E]/50 sm:px-4 sm:py-2.5"
          >
            <LogOut className="mr-1.5 inline h-4 w-4 text-white" strokeWidth={2.5} />
            Logout
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
      {/* Sidebar — nav only; width changes on minimize */}
      <aside
        className={`flex shrink-0 flex-col overflow-hidden bg-gradient-to-b from-cyan-900 to-teal-900 shadow-lg transition-[width] duration-200 ease-in-out ${
          sidebarCollapsed ? 'w-16' : 'w-60'
        }`}
      >
        <nav
          className={`box-border w-full min-w-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden ${
            sidebarCollapsed ? 'p-1 pt-2' : 'p-3 pt-3'
          }`}
        >
          <div
            className={`box-border flex w-full min-w-0 items-center ${
              sidebarCollapsed ? 'justify-center py-0.5' : 'justify-between gap-2 px-0 py-1'
            }`}
          >
            {!sidebarCollapsed && (
              <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-cyan-300/90">
                Reports
              </p>
            )}
            <SidebarToggleButton collapsed={sidebarCollapsed} onClick={toggleSidebar} />
          </div>
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={view === 'payrollHoursReport'}
            onClick={() => setView('payrollHoursReport')}
            icon={BarChart3}
            label="Payroll hours (time type)"
          />
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={view === 'timeCardsList'}
            onClick={() => setView('timeCardsList')}
            icon={LayoutList}
            label="HCM time cards (list)"
          />
          {categories.map((cat) => (
            <SidebarNavButton
              key={cat}
              collapsed={sidebarCollapsed}
              active={view === 'reports' && category === cat}
              onClick={() => {
                setCategory(cat);
                setView('reports');
              }}
              icon={FolderOpen}
              label={cat}
            />
          ))}
          <SidebarSectionLabel collapsed={sidebarCollapsed} spaced>
            Time sheets
          </SidebarSectionLabel>
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={view === 'upload'}
            onClick={() => setView('upload')}
            icon={Upload}
            label="Accutime (StartTime/StopTime)"
          />
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={view === 'niceUpload'}
            onClick={() => setView('niceUpload')}
            icon={Upload}
            label="NICE (EventTime/EventState)"
          />
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={view === 'hcmTimeCardDelete'}
            onClick={() => {
              setHcmTimeCardDeletePrefill(null);
              setView('hcmTimeCardDelete');
            }}
            icon={Trash2}
            label="Delete HCM time cards"
          />
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={view === 'help'}
            onClick={() => setView('help')}
            icon={CircleHelp}
            label="Help (time sheets)"
          />
          <SidebarSectionLabel collapsed={sidebarCollapsed} spaced>
            Time Punches History
          </SidebarSectionLabel>
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={view === 'reportHistory' && reportHistoryFolder === 'logs'}
            onClick={() => {
              setView('reportHistory');
              setReportHistoryFolder('logs');
            }}
            icon={FileText}
            label="Logs"
          />
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={view === 'reportHistory' && reportHistoryFolder === 'error'}
            onClick={() => {
              setView('reportHistory');
              setReportHistoryFolder('error');
            }}
            icon={AlertCircle}
            label="Error"
          />
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={view === 'reportHistory' && reportHistoryFolder === 'archive'}
            onClick={() => {
              setView('reportHistory');
              setReportHistoryFolder('archive');
            }}
            icon={Archive}
            label="Archive"
          />
          <SidebarSectionLabel collapsed={sidebarCollapsed} spaced>
            Connections
          </SidebarSectionLabel>
          <SidebarNavButton
            collapsed={sidebarCollapsed}
            active={false}
            onClick={() => {
              setView('connections');
              setOpenNewConnectionModal(true);
              setSelectedConnectionId(null);
            }}
            icon={Plus}
            label="New connection"
          />
          {connectionsList.map((conn) => (
            <SidebarNavButton
              key={conn.id}
              collapsed={sidebarCollapsed}
              active={view === 'connections' && selectedConnectionId === conn.id}
              onClick={() => {
                setView('connections');
                setSelectedConnectionId(conn.id);
                setOpenNewConnectionModal(false);
              }}
              icon={Link2}
              label={conn.connection_name || 'Unnamed'}
            />
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <main className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-cyan-50/50 backdrop-blur-sm">
        <div className="flex min-h-0 flex-1 flex-col overflow-auto p-6">
          {view === 'upload' ? (
            <div className="grid gap-6 md:grid-cols-3 items-start">
              <div className="md:col-span-2 min-w-0">
                <TimeSheetUploadView
                  user={user}
                  uploadFile={uploadFile}
                  setUploadFile={setUploadFile}
                  uploading={uploading}
                  setUploading={setUploading}
                  uploadResult={uploadResult}
                  setUploadResult={setUploadResult}
                  uploadError={uploadError}
                  setUploadError={setUploadError}
                  uploadApiUrl="/api/upload-time-sheet"
                  title="Accutime Time Sheet Upload"
                  onUploadComplete={() => setReportHistoryRefresh((k) => k + 1)}
                  expectedColumns={
                    <>
                      <strong>PersonHCM</strong> or <strong>Employee ID</strong>, <strong>Date</strong>, <strong>StartTime</strong>, <strong>StopTime</strong> (optional: Agent_No, EventState); or one punch per row:{' '}
                      <strong>Timestamp Wall Time</strong> (ISO, the punch instant), <strong>Punch Type</strong> In = start time / Out = stop time for pairing, <strong>Employee ID</strong>.
                    </>
                  }
                  showReviewStep
                  previewApiUrl="/api/time-sheet-preview"
                  onRequestOracleLogin={() => setShowLoginModal(true)}
                  connectionsList={connectionsList}
                  selectedConnectionId={selectedConnectionId}
                  onSelectConnectionId={setSelectedConnectionId}
                  showPunchExportInfo={false}
                  showUploadDestination={false}
                />
              </div>
              <div className="md:col-span-1 space-y-6">
                <AtsUploadHeaderContext
                  user={user}
                  connectionsList={connectionsList}
                  selectedConnectionId={selectedConnectionId}
                />
                <ExternalIdentifierLookup
                  connectionsList={connectionsList}
                  selectedConnectionId={selectedConnectionId}
                />
              </div>
            </div>
          ) : view === 'help' ? (
            <TimeSheetsHelpView apiBase={API_BASE} apiBaseSource={API_BASE_SOURCE} />
          ) : view === 'hcmTimeCardDelete' ? (
            <TimeCardHcmDeletePanel
              apiBase={API_BASE}
              user={user}
              connectionsList={connectionsList}
              selectedConnectionId={selectedConnectionId}
              onSelectConnectionId={setSelectedConnectionId}
              prefill={hcmTimeCardDeletePrefill}
              onPrefillConsumed={() => setHcmTimeCardDeletePrefill(null)}
            />
          ) : view === 'timeCardsList' ? (
            <TimeCardsListPanel
              api={API}
              user={user}
              connectionsList={connectionsList}
              selectedConnectionId={selectedConnectionId}
              onSelectConnectionId={setSelectedConnectionId}
              onRequestOracleLogin={() => setShowLoginModal(true)}
              listPrefill={timeCardsListPrefill}
              onListPrefillConsumed={() => setTimeCardsListPrefill(null)}
              onOpenDeleteWithRange={(o) => {
                setHcmTimeCardDeletePrefill(o);
                setView('hcmTimeCardDelete');
              }}
            />
          ) : view === 'niceUpload' ? (
            <div className="grid gap-6 md:grid-cols-3 items-start">
              <div className="md:col-span-2 min-w-0">
                <TimeSheetUploadView
                  user={user}
                  uploadFile={niceUploadFile}
                  setUploadFile={setNiceUploadFile}
                  uploading={niceUploading}
                  setUploading={setNiceUploading}
                  uploadResult={niceUploadResult}
                  setUploadResult={setNiceUploadResult}
                  uploadError={niceUploadError}
                  setUploadError={setNiceUploadError}
                  uploadApiUrl="/api/upload-nice-time-sheet"
                  title="NICE Time Sheet Upload"
                  onUploadComplete={() => setReportHistoryRefresh((k) => k + 1)}
                  expectedColumns={
                    <>
                      <strong>Agent_No</strong>, <strong>Date</strong>, <strong>EventTime</strong> (24-hour), <strong>EventState</strong> (LoggedIn, LoggedOut, LunchOut, LunchIn),{' '}
                      <strong>HCM_Pers</strong> (or PersonHCM).
                    </>
                  }
                  showReviewStep
                  previewApiUrl="/api/nice-time-sheet-preview"
                  onRequestOracleLogin={() => setShowLoginModal(true)}
                  connectionsList={connectionsList}
                  selectedConnectionId={selectedConnectionId}
                  onSelectConnectionId={setSelectedConnectionId}
                  showUploadDestination={false}
                />
              </div>
              <div className="md:col-span-1 space-y-6">
                <NiceUploadSideContext
                  user={user}
                  connectionsList={connectionsList}
                  selectedConnectionId={selectedConnectionId}
                />
                <ExternalIdentifierLookup
                  connectionsList={connectionsList}
                  selectedConnectionId={selectedConnectionId}
                />
              </div>
            </div>
          ) : view === 'reportHistory' ? (
            <ReportHistory folder={reportHistoryFolder} refreshTrigger={reportHistoryRefresh} />
          ) : view === 'payrollHoursReport' ? (
            <PayrollHoursByTimeTypeReport
              api={API}
              user={user}
              connectionsList={connectionsList}
              selectedConnectionId={selectedConnectionId}
              onSelectConnectionId={setSelectedConnectionId}
              onRequestOracleLogin={() => setShowLoginModal(true)}
              onOpenTimeCardList={({ startDate, endDate, personNumber }) => {
                setTimeCardsListPrefill({ startDate, endDate, personNumber: personNumber || null });
                setView('timeCardsList');
              }}
              onOpenHcmTimeCardDelete={({ startDate, endDate, personNumber }) => {
                setHcmTimeCardDeletePrefill({ startDate, stopDate: endDate, personNumber: personNumber || '' });
                setView('hcmTimeCardDelete');
              }}
            />
          ) : view === 'connections' ? (
            <Connections
              connectionsList={connectionsList}
              onRefreshConnections={refreshConnections}
              selectedConnectionId={selectedConnectionId}
              onSelectConnectionId={setSelectedConnectionId}
              openNewConnectionModal={openNewConnectionModal}
              onOpenNewConnectionHandled={() => setOpenNewConnectionModal(false)}
            />
          ) : (
            <>
              {loading && (
                <>
                  <p className="mb-4 text-sm text-slate-500">Loading catalog…</p>
                  <CatalogSkeleton />
                </>
              )}

              {!loading && (
                <>
                  <p className="mb-4 text-sm text-slate-600">
                    {filteredReports.length} report{filteredReports.length !== 1 ? 's' : ''} found
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredReports.map((r) => {
                  const isFavorite = favorites.includes(r.path);
                  const isRecipe = isSqlRecipeReport(r);
                  return (
                    <div
                      key={r.path}
                      className="card-panel flex flex-col p-5 transition-shadow hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="min-w-0 flex-1 font-semibold text-slate-800 line-clamp-1" title={r.name}>
                          {r.name || r.path}
                        </h3>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(r.path); }}
                          className="shrink-0 rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-400"
                          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Star
                            className={`h-5 w-5 ${isFavorite ? 'fill-amber-400 text-amber-400' : ''}`}
                          />
                        </button>
                      </div>
                      <p className="mt-1.5 flex-1 text-sm text-slate-500 line-clamp-2" title={isRecipe ? (r.recipeId || r.path) : r.path}>
                        {isRecipe ? (r.recipeId || r.path) : r.path}
                      </p>
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        {isRecipe ? (
                          <button
                            type="button"
                            disabled={!r.sql}
                            onClick={() => setRunModalSqlRecipe(r)}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-cyan-600 to-teal-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:from-cyan-700 hover:to-teal-700 disabled:opacity-50"
                          >
                            <Play className="h-4 w-4" /> {r.sql ? 'Run' : 'SQL unavailable'}
                          </button>
                        ) : (
                          <>
                            <select
                              value={cardFormat}
                              onChange={(e) => setCardFormat(e.target.value)}
                              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 shadow-sm focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="pdf">PDF</option>
                              <option value="xlsx">XLSX</option>
                              <option value="csv">CSV</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => setRunModalReport(r)}
                              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-cyan-600 to-teal-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:from-cyan-700 hover:to-teal-700"
                            >
                              <Play className="h-4 w-4" /> Run
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {filteredReports.length === 0 && (
                <div className="card-panel p-10 text-center">
                  <p className="text-slate-600">
                    {timeReports.length === 0
                      ? 'No time-related reports found.'
                      : 'No reports match your filters.'}
                  </p>
                  {user && timeReports.length === 0 && (
                    <p className="mt-3 text-sm text-slate-500 max-w-md mx-auto">
                      Reports are loaded from the <strong>/Custom/SAAS Tool/V1</strong> folder in Oracle BI Publisher.
                      If your instance uses a different folder or you don’t see any reports here, add or
                      publish reports under /Custom/SAAS Tool/V1 in BI Publisher, or contact your admin to confirm
                      the report catalog path.
                    </p>
                  )}
                  {user && timeReports.length === 0 && (
                    <a
                      href="/api/catalog-debug"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-block text-sm text-blue-600 hover:underline"
                    >
                      See raw API response
                    </a>
                  )}
                </div>
              )}
                </>
              )}
              </>
            )}
        </div>
      </main>
      </div>

      {/* Run report modal */}
      {runModalReport && (
        <RunReportModal
          report={runModalReport}
          initialFormat={cardFormat}
          onClose={() => setRunModalReport(null)}
          onRunComplete={() => setReportHistoryRefresh((k) => k + 1)}
        />
      )}

      {runModalSqlRecipe && (
        <RunSqlRecipeModal
          key={runModalSqlRecipe.path}
          report={runModalSqlRecipe}
          connectionId={
            connectionsList.some((c) => c.id === selectedConnectionId)
              ? selectedConnectionId
              : connectionsList[0]?.id
          }
          onClose={() => setRunModalSqlRecipe(null)}
        />
      )}

      {/* Login modal */}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onSuccess={(u) => {
            setUser(u);
            setShowLoginModal(false);
          }}
        />
      )}
    </div>
  );
}
